import { useCallback, useEffect, useState } from 'react';
import type { NavigateFunction } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import InspectionStatus from '@/enums/InspectionStatus.enum';
import { Inspection } from '@/types/Inspection.type';
import { systemConfig } from '@/config/systemConfig';
import { useAppSelector } from '@/store/hooks';
import { RootState } from '@/store/rootReducer';
import { getCurrentPosition, watchBestPosition } from '@/utils/geolocation';
import { haversineDistanceMetres } from '@/utils/geoDistance';
import { locationVerificationDraftStorage } from '@/store/idbStorage/locationVerificationDraftStorage';
import { fetchActiveFactoryVisitSession } from '@/utils/factoryVisitSession.api';
import {
  buildVerificationResumePath,
  canAutoSkipFactoryVerification,
  evaluateFactoryVerificationCaptureDecision,
  inspectionRequiresLocationVerification,
} from '@/utils/locationVerification.utils';
import { isInspectionStatus } from '@/utils/inspectionStart.utils';
import {
  DeviceLocation,
  getFactoryRegisteredCoords,
} from '@/utils/startInspectionGpsVerification.utils';
import {
  getFactoryDistanceThresholdMeters,
  getMinGpsAccuracyMeters,
} from '@/utils/factoryVerificationConfig.util';
import { FactoryVisitSession } from '@/types/LocationVerification.type';
import {
  FactoryVerificationStartFlowTrigger,
  logFactoryVerificationStartFlowDecision,
} from '@/utils/factoryVerificationLogger.util';
import { resolveStartInspectionGpsAction } from '@/utils/startInspectionGpsResolution.util';

const PRECISE_GEO_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  maximumAge: 0,
  timeout: 10000,
};

const FALLBACK_GEO_OPTIONS: PositionOptions = {
  enableHighAccuracy: false,
  maximumAge: 60 * 1000,
  timeout: 8000,
};

async function resolveStartInspectionVerificationPath(params: {
  inspectionId: string;
  inspection: Inspection | null;
  factoryVerificationEnabledForInspection: boolean;
  inspectorId: string | undefined;
  isOnline: boolean;
  deviceLocation: DeviceLocation | null;
  distanceThresholdM: number;
  trigger: FactoryVerificationStartFlowTrigger;
}): Promise<string | null> {
  const {
    inspectionId,
    inspection,
    factoryVerificationEnabledForInspection,
    inspectorId,
    isOnline,
    deviceLocation,
    distanceThresholdM,
    trigger,
  } = params;
  if (!inspection) {
    return null;
  }
  const draft = await locationVerificationDraftStorage.get(inspectionId);
  const captureDecision = evaluateFactoryVerificationCaptureDecision({
    inspection,
    factoryVerificationEnabled: factoryVerificationEnabledForInspection,
    draft,
    deviceLocation,
    distanceThresholdM,
  });
  if (!captureDecision.requiresCapture) {
    logFactoryVerificationStartFlowDecision(trigger, {
      inspectionId,
      flowAction: 'stayOnStart',
      captureDecision,
      factoryId: inspection.factory?.id,
    });
    return null;
  }
  const factoryLookup =
    inspection.factory?.id && inspectorId
      ? await fetchActiveFactoryVisitSession({
          factoryId: inspection.factory.id,
          tenantId: systemConfig.tenantId,
          inspectorId,
          isOnline,
        })
      : { reuseDays: 1, session: null };
  if (
    canAutoSkipFactoryVerification({
      draft,
      session: factoryLookup.session,
      reuseDays: factoryLookup.reuseDays,
    })
  ) {
    logFactoryVerificationStartFlowDecision(trigger, {
      inspectionId,
      flowAction: 'autoSkipReuse',
      skipReason: 'reusableVisitSession',
      reuseDays: factoryLookup.reuseDays,
      serverSessionId: factoryLookup.session?.serverSessionId,
      factoryId: inspection.factory?.id,
    });
    return null;
  }
  const path = buildVerificationResumePath(
    inspectionId,
    draft,
    factoryLookup.session,
    factoryLookup.reuseDays
  );
  logFactoryVerificationStartFlowDecision(trigger, {
    inspectionId,
    flowAction: 'navigateVerification',
    path,
    captureDecision,
    factoryId: inspection.factory?.id,
  });
  return path;
}

export type StartInspectionGpsResolution =
  | { action: 'skip' }
  | { action: 'navigate'; path: string }
  | { action: 'start' }
  | { action: 'startWithReusedEvidence'; session: FactoryVisitSession };

export interface UseStartInspectionGpsVerificationParams {
  inspectionId: string | undefined;
  inspection: Inspection | null;
  factoryVerificationEnabled: boolean;
  hasSavedInspection: boolean;
  inspectorId: string | undefined;
  isOnline: boolean;
  navigate: NavigateFunction;
}

export interface UseStartInspectionGpsVerificationResult {
  isEnabled: boolean;
  isRequestingLocation: boolean;
  isLocationGateActive: boolean;
  canStart: boolean;
  locationMessageKey: string | null;
  deviceLocation: DeviceLocation | null;
  registeredCoords: { lat: number; lng: number } | null;
  currentCoords: { lat: number; lng: number } | null;
  currentAccuracyMetres: number | null;
  retryLocation: () => void;
  resolveStartWithGps: () => Promise<StartInspectionGpsResolution>;
  getCoordsForInspectionUpdate: () => { latitude: number; longitude: number } | null;
}

export function useStartInspectionGpsVerification(
  params: UseStartInspectionGpsVerificationParams
): UseStartInspectionGpsVerificationResult {
  const {
    inspectionId,
    inspection,
    factoryVerificationEnabled,
    hasSavedInspection,
    inspectorId,
    isOnline,
    navigate,
  } = params;
  const { t } = useTranslation();
  const appConfig = useAppSelector((state: RootState) => state.appConfig);
  const factoryDistanceThresholdM = getFactoryDistanceThresholdMeters(appConfig);
  const minGpsAccuracyMeters = getMinGpsAccuracyMeters(appConfig);
  const [deviceLocation, setDeviceLocation] = useState<DeviceLocation | null>(null);
  const [deviceLocationReceived, setDeviceLocationReceived] = useState(false);
  const [locationMessageKey, setLocationMessageKey] = useState<string | null>(null);
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const verificationApplicable = inspectionRequiresLocationVerification(inspection);
  const isEnabled = Boolean(factoryVerificationEnabled && verificationApplicable);
  const registeredCoords = getFactoryRegisteredCoords(inspection);

  const requestDeviceLocation = useCallback(
    async (mode: 'initial' | 'retry' = 'initial'): Promise<DeviceLocation | null> => {
      if (!navigator?.geolocation) {
        setDeviceLocationReceived(true);
        if (isEnabled && inspectionId) {
          logFactoryVerificationStartFlowDecision('gpsResolved', {
            inspectionId,
            mode,
            flowAction: 'navigateVerification',
            skipReason: 'geolocationNotSupported',
          });
        }
        return null;
      }
      setIsRequestingLocation(true);
      setLocationMessageKey(null);
      const applyPosition = (position: GeolocationPosition): DeviceLocation => {
        const { latitude, longitude, accuracy } = position.coords;
        const roundedAccuracy =
          typeof accuracy === 'number' && Number.isFinite(accuracy) ? Math.round(accuracy) : null;
        const loc: DeviceLocation = {
          latitude,
          longitude,
          accuracyMetres: roundedAccuracy,
        };
        setDeviceLocation(loc);
        setDeviceLocationReceived(true);
        setLocationMessageKey(null);
        setIsRequestingLocation(false);
        if (isEnabled && inspectionId && inspection) {
          const captureDecision = evaluateFactoryVerificationCaptureDecision({
            inspection,
            factoryVerificationEnabled: isEnabled,
            draft: null,
            deviceLocation: loc,
            distanceThresholdM: factoryDistanceThresholdM,
          });
          logFactoryVerificationStartFlowDecision('gpsResolved', {
            inspectionId,
            mode,
            captureDecision,
            factoryId: inspection.factory?.id,
          });
        }
        if (mode === 'retry') {
          const registered = getFactoryRegisteredCoords(inspection);
          if (registered) {
            const distanceM = haversineDistanceMetres(
              latitude,
              longitude,
              registered.lat,
              registered.lng
            );
            const roundedDistance = Math.round(distanceM);
            const distanceMessage = t('gps_distance_from_registered_success', {
              distance: roundedDistance,
            });
            if (roundedDistance > factoryDistanceThresholdM) {
              toast.warn(distanceMessage);
            } else {
              toast.success(distanceMessage);
            }
          }
        }
        return loc;
      };
      const completeGpsAttemptWithoutFix = (skipReason: string, geoErrorCode?: number) => {
        setIsRequestingLocation(false);
        setDeviceLocationReceived(true);
        if (isEnabled && inspectionId) {
          logFactoryVerificationStartFlowDecision('gpsResolved', {
            inspectionId,
            mode,
            flowAction: 'navigateVerification',
            skipReason,
            ...(geoErrorCode != null ? { geoErrorCode } : {}),
          });
        }
      };
      try {
        const watchMs = mode === 'retry' ? 30_000 : 8_000;
        const bestPrecise = await watchBestPosition({
          maxWatchMs: watchMs,
          stopWhenAccuracyLTE: minGpsAccuracyMeters,
          options: PRECISE_GEO_OPTIONS,
        });
        if (bestPrecise) {
          return applyPosition(bestPrecise);
        }
        const fallbackPos = await getCurrentPosition(FALLBACK_GEO_OPTIONS);
        return applyPosition(fallbackPos);
      } catch (error: unknown) {
        const geoError = error as { code?: number };
        if (geoError?.code === 1) {
          setLocationMessageKey('enable_location_on_device');
          completeGpsAttemptWithoutFix('gpsPermissionDenied', geoError.code);
          return null;
        }
        if (mode === 'retry') {
          toast.warn(t('gps_coordinates_not_obtained_verification_required'));
          setLocationMessageKey(null);
          completeGpsAttemptWithoutFix('gpsUnavailableAfterRetry');
          return null;
        }
        setLocationMessageKey('enable_location_on_device');
        completeGpsAttemptWithoutFix('gpsUnavailable');
        return null;
      }
    },
    [factoryDistanceThresholdM, inspection, inspectionId, isEnabled, minGpsAccuracyMeters, t]
  );

  useEffect(() => {
    setLocationMessageKey(null);
    if (!isEnabled) {
      setDeviceLocationReceived(true);
      return;
    }
    if (!navigator?.geolocation) {
      setDeviceLocationReceived(true);
      return;
    }
    setDeviceLocationReceived(false);
    void requestDeviceLocation('initial');
  }, [isEnabled, requestDeviceLocation]);

  useEffect(() => {
    let cancelled = false;
    const redirectToVerificationIfNeeded = async () => {
      if (!inspectionId || !inspection || !isEnabled || !deviceLocationReceived) {
        return;
      }
      const verificationPath = await resolveStartInspectionVerificationPath({
        inspectionId,
        inspection,
        factoryVerificationEnabledForInspection: isEnabled,
        inspectorId,
        isOnline,
        deviceLocation,
        distanceThresholdM: factoryDistanceThresholdM,
        trigger: 'autoRedirect',
      });
      if (cancelled || !verificationPath) {
        return;
      }
      navigate(verificationPath, { replace: true });
    };
    void redirectToVerificationIfNeeded();
    return () => {
      cancelled = true;
    };
  }, [
    inspectionId,
    inspection,
    isEnabled,
    navigate,
    isOnline,
    inspectorId,
    deviceLocation,
    deviceLocationReceived,
    factoryDistanceThresholdM,
  ]);

  const canStart =
    !isEnabled || hasSavedInspection || (deviceLocationReceived && deviceLocation != null);

  const isLocationGateActive = isEnabled && !hasSavedInspection && !deviceLocationReceived;

  const resolveStartWithGps = useCallback(async (): Promise<StartInspectionGpsResolution> => {
    if (!inspectionRequiresLocationVerification(inspection)) {
      logFactoryVerificationStartFlowDecision('startButton', {
        inspectionId,
        flowAction: 'skipNotApplicable',
        skipReason: 'controlPointNotApplicable',
      });
      return { action: 'skip' };
    }
    if (isEnabled && inspectionId) {
      const draft = await locationVerificationDraftStorage.get(inspectionId);
      const factoryLookup =
        inspection?.factory?.id && inspectorId
          ? await fetchActiveFactoryVisitSession({
              factoryId: inspection.factory.id,
              tenantId: systemConfig.tenantId,
              inspectorId,
              isOnline,
            })
          : { reuseDays: 1, session: null };
      const startAction = resolveStartInspectionGpsAction({
        inspection,
        factoryVerificationEnabled: isEnabled,
        draft,
        factorySession: factoryLookup.session,
        reuseDays: factoryLookup.reuseDays,
        deviceLocation,
        distanceThresholdM: factoryDistanceThresholdM,
      });
      if (startAction.action === 'startWithReusedEvidence') {
        logFactoryVerificationStartFlowDecision('startButton', {
          inspectionId,
          flowAction: 'autoSkipReuse',
          skipReason: 'reusableVisitSession',
          serverSessionId: startAction.session.serverSessionId,
          reuseDays: factoryLookup.reuseDays,
          factoryId: inspection?.factory?.id,
        });
        return startAction;
      }
      if (startAction.action === 'skip') {
        return startAction;
      }
      if (startAction.action === 'start' && !startAction.captureRequired) {
        const captureDecision = evaluateFactoryVerificationCaptureDecision({
          inspection,
          factoryVerificationEnabled: isEnabled,
          draft,
          deviceLocation,
          distanceThresholdM: factoryDistanceThresholdM,
        });
        logFactoryVerificationStartFlowDecision('startButton', {
          inspectionId,
          flowAction: 'startDirect',
          captureDecision,
          factoryId: inspection?.factory?.id,
        });
        return { action: 'start' };
      }
      const verificationPath = await resolveStartInspectionVerificationPath({
        inspectionId,
        inspection,
        factoryVerificationEnabledForInspection: isEnabled,
        inspectorId,
        isOnline,
        deviceLocation,
        distanceThresholdM: factoryDistanceThresholdM,
        trigger: 'startButton',
      });
      if (verificationPath) {
        return { action: 'navigate', path: verificationPath };
      }
    }
    const isPlanned = isInspectionStatus(inspection?.inspectionStatusId, InspectionStatus.PLANNED);
    if (isPlanned && !deviceLocation) {
      await requestDeviceLocation('initial');
    }
    if (isPlanned && isEnabled && inspectionId && inspection) {
      const verificationPath = await resolveStartInspectionVerificationPath({
        inspectionId,
        inspection,
        factoryVerificationEnabledForInspection: isEnabled,
        inspectorId,
        isOnline,
        deviceLocation,
        distanceThresholdM: factoryDistanceThresholdM,
        trigger: 'startButton',
      });
      if (verificationPath) {
        return { action: 'navigate', path: verificationPath };
      }
      const captureDecision = evaluateFactoryVerificationCaptureDecision({
        inspection,
        factoryVerificationEnabled: isEnabled,
        draft: await locationVerificationDraftStorage.get(inspectionId),
        deviceLocation,
        distanceThresholdM: factoryDistanceThresholdM,
      });
      logFactoryVerificationStartFlowDecision('startButton', {
        inspectionId,
        flowAction: 'startDirect',
        captureDecision,
        factoryId: inspection.factory?.id,
      });
      return { action: 'start' };
    }
    logFactoryVerificationStartFlowDecision('startButton', {
      inspectionId,
      flowAction: 'startDirect',
      skipReason: 'plannedGpsGateNotApplicable',
    });
    return { action: 'start' };
  }, [
    deviceLocation,
    factoryDistanceThresholdM,
    inspection,
    inspectionId,
    inspectorId,
    isEnabled,
    isOnline,
    requestDeviceLocation,
  ]);

  const getCoordsForInspectionUpdate = useCallback((): {
    latitude: number;
    longitude: number;
  } | null => {
    if (deviceLocation?.latitude == null || deviceLocation?.longitude == null) {
      return null;
    }
    return {
      latitude: deviceLocation.latitude,
      longitude: deviceLocation.longitude,
    };
  }, [deviceLocation]);

  return {
    isEnabled,
    isRequestingLocation,
    isLocationGateActive,
    canStart,
    locationMessageKey,
    deviceLocation,
    registeredCoords,
    currentCoords:
      deviceLocation?.latitude != null && deviceLocation?.longitude != null
        ? { lat: deviceLocation.latitude, lng: deviceLocation.longitude }
        : null,
    currentAccuracyMetres: deviceLocation?.accuracyMetres ?? null,
    retryLocation: () => {
      void requestDeviceLocation('retry');
    },
    resolveStartWithGps,
    getCoordsForInspectionUpdate,
  };
}
